import moment from 'moment';
import { mix } from 'polished';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as graphql from 'generated/graphql';

import {
	ArchiveIcon,
	ChevronLeftIcon,
	CloseIcon,
	ConversationIcon,
	DraftsIcon,
	UndoIcon,
} from '@treams/treams-icons';
import { Link, useHistory } from '@treams/treams-routes';
import {
	Author,
	Button,
	Callout,
	Container,
	Heading,
	IconButton,
	Paragraph,
	Spinner,
	Stat,
	Subheading,
	Tags,
	Title,
	Todo,
	Toolbar,
} from '@treams/treams-ui';
import toast, { Toaster } from 'react-hot-toast';

import {
	Goal as GoalEntity,
	GoalFeasibility,
	GoalOwnerType,
	User,
	useArchiveGoalMutation,
	useIndividualGoalQuery,
	useRestoreGoalMutation,
	useUpdateGoalMutation,
} from 'generated/graphql';

import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

import Comments from 'comments/Comments';
import { ModalContext } from '../navigation/ModalNavigation';
import GoalAuditTrail from './GoalAuditTrail';
import Subgoals, { SubgoalsContext } from './subgoals/Subgoals';
import SubgoalsContainer from './subgoals/SubgoalsContainer';
import { useLocation } from 'react-router';

export function FeasibilityTag(props: {
	feasibility: GoalFeasibility;
	label?: string;
	plain?: boolean;
}) {
	const { t } = useTranslation();

	const feasibilityMap = {
		[GoalFeasibility.CloseCall]: {
			label: t('goals.show.feasibility.closeCall'),
			color: 'yellow',
		},
		[GoalFeasibility.WillMakeIt]: {
			label: t('goals.show.feasibility.willMakeIt'),
			color: 'green',
		},
		[GoalFeasibility.WontMakeIt]: {
			label: t('goals.show.feasibility.wontMakeIt'),
			color: 'red',
		},
	};

	const { feasibility } = props;

	return (
		<Tags.Item
			status={feasibilityMap[feasibility].color}
			label={props.label ?? t('goals.show.feasibility.label')}
			value={feasibilityMap[feasibility].label}
			plain={props.plain}
		/>
	);
}

const Strikethrough = styled.span`
	background: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.green)};
	color: ${(props) => props.theme.colors.green};
`;

function GoalCompletionToastContent(props: { goalID: string | undefined; onArchive: () => void }) {
	const { t } = useTranslation();

	const dismiss = () => {
		if (props.goalID) {
			toast.dismiss(props.goalID);
		}
	};

	return (
		<Box m={3}>
			<Box mb={'22px'} flexGrow={1}>
				<Subheading mb={1}>{t('goals.show.completionToast.title')}</Subheading>
				<Paragraph>{t('goals.show.completionToast.description')}</Paragraph>
			</Box>
			<Flex justifyContent={'flex-end'} style={{ gap: '12px' }}>
				<Button
					title={t('goals.show.toolbar.archive')}
					type="destructive"
					small
					onClick={() => {
						dismiss();
						props.onArchive();
					}}
				/>
				<IconButton icon={<CloseIcon />} type="plain" onClick={dismiss} />
			</Flex>
		</Box>
	);
}

export default function Goal(props: { userID?: string; goalID: string }) {
	const comments = useRef<HTMLDivElement>(null);

	const { data } = useIndividualGoalQuery({ variables: { id: props.goalID } });

	const [archive, { loading: isLoadingArchive }] = useArchiveGoalMutation();
	const [restore, { loading: isLoadingRestore }] = useRestoreGoalMutation();
	const [update, { loading: isLoadingUpdate }] = useUpdateGoalMutation();

	const { localize } = useLocalize();
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();

	const hasAudit = useFeature(graphql.FeatureName.GoalsAudit);

	const isArchived = !!data?.goal.isArchived;

	const onArchiveRestore = async () => {
		if (isArchived) {
			await restore({ variables: { input: { id: props.goalID } } });
			return;
		}
		await archive({ variables: { input: { id: props.goalID } } });
	};

	const returnUrl = (() => {
		const params = new URLSearchParams(history.location.search);
		if (params.has('return') && params.get('return')?.startsWith('/')) {
			return params.get('return')!;
		}

		// Use navigation state if available
		if (location.state?.returnURL) {
			return location.state.returnURL;
		}

		// View team/department goal
		if (
			data?.goal.ownerType === GoalOwnerType.Department ||
			data?.goal.ownerType === GoalOwnerType.Team
		) {
			return '/goals/collective';
		}

		// View workspace goal
		if (data?.goal.ownerType === GoalOwnerType.Workspace) {
			return '/goals/organization';
		}

		// View personal goal of other user
		if (props.userID) {
			return `/users/${props.userID}/goals`;
		}

		// View personal goal of viewer
		return '/goals';
	})();

	const handleProgressUpdate = () => {
		if (data) {
			const { goal } = data;

			let progressOverride: number | undefined = 1.0;

			if (goal.progress === 1.0) {
				progressOverride = undefined;
			}

			const {
				id,
				title,
				description,
				checklistItems,
				dueDate,
				feasibility,
				linkedGoalDrivers,
				competency,
			} = goal;

			update({
				variables: {
					input: {
						id: id,
						title: title,
						description: description,
						checklistItems: checklistItems.map((item) => ({
							id: item.id,
							description: item.description,
							progress:
								item.progress.lower != null
									? {
											type: item.progress.type,
											value: item.progress.value,
											lower: item.progress.lower,
											upper: item.progress.upper,
									  }
									: {
											type: item.progress.type,
											value: item.progress.value,
									  },
							title: item.title,
							dueDate: item.dueDate,
						})),
						dueDate: dueDate,
						feasibility: feasibility ?? undefined,
						linkedGoalDriverIDs: linkedGoalDrivers.map((driver) => driver.id),
						linkedGoalIDs: [],
						competency: competency
							? {
									competencyID: competency.competency.id,
									competencyLevelIndex: competency.competencyLevelIndex,
							  }
							: undefined,
						progressOverride,
					},
				},
			});
		}
	};

	const progress = data?.goal.progress === undefined ? 0 : data.goal.progress;

	const checkState =
		progress === 1.0
			? true
			: // If partially complete, will return null for indeterminate state for checkbox
			progress > 0.0 && progress < 1.0
			? null
			: false;

	// Don't show toast on initial page load
	const [showCompletionToast, setShowCompletionToast] = useState(false);

	useEffect(() => {
		if (checkState && !isArchived && showCompletionToast) {
			toast(
				<GoalCompletionToastContent
					goalID={data?.goal.id}
					onArchive={() => {
						if (!isArchived) {
							onArchiveRestore();
						}
					}}
				/>,
				{
					id: data?.goal.id,
					duration: 5000,
				},
			);

			setShowCompletionToast(false);
		} else {
			if (data) {
				toast.dismiss(data.goal.id);
				setShowCompletionToast(true);
			}
		}

		return () => {
			// Remove toast on unmount
			if (data) {
				toast.remove(data.goal.id);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkState, data?.goal.id]);

	return (
		<>
			<Container py={4}>
				<Toaster
					position="bottom-center"
					toastOptions={{
						style: {
							maxWidth: '480px',
							borderRadius: '10px',
							padding: '6px 0',
							boxShadow: '0px 8px 16px rgba(108, 118, 128, 0.25)',
						},
					}}
				/>
				<Toolbar>
					<ModalContext.Consumer>
						{(value) =>
							!value.isModal && (
								<Button
									icon={<ChevronLeftIcon />}
									link={returnUrl}
									title={t('goals.show.toolbar.back')}
									type="secondary"
								/>
							)
						}
					</ModalContext.Consumer>
					{data?.goal.viewerCanUpdate && (
						<>
							<Toolbar.Spacing />
							<Button
								title={
									isArchived ? t('goals.show.toolbar.restore') : t('goals.show.toolbar.archive')
								}
								type="destructive"
								icon={isArchived ? <UndoIcon /> : <ArchiveIcon />}
								loading={isLoadingArchive || isLoadingRestore}
								onClick={() => {
									onArchiveRestore();
								}}
							/>
							<Button
								title={t('goals.show.toolbar.edit')}
								icon={<DraftsIcon />}
								link={
									props.userID
										? `/users/${props.userID}/goals/${props.goalID}/edit`
										: `/goals/${props.goalID}/edit`
								}
							/>
						</>
					)}
				</Toolbar>

				{isArchived && (
					<Callout
						mt={3}
						color="red"
						icon={<ArchiveIcon />}
						title={t('goals.show.archived.title')}
						description={t('goals.show.archived.description')}
					/>
				)}

				{data ? (
					<Box mt={4}>
						{data.goal.parent && (
							<Paragraph mb={2}>
								{t('goals.show.contributes')}{' '}
								<Link to={`/goals/${data.goal.parent.id}`}>
									{localize(data.goal.parent.title).text}
								</Link>
							</Paragraph>
						)}
						<Flex alignItems="center" mb={3}>
							<Box flexGrow={0} flexShrink={0} mr={3}>
								{isLoadingUpdate ? (
									<Box p={1}>
										<Spinner />
									</Box>
								) : (
									<span title="test">
										<Todo checked={checkState} onChange={() => handleProgressUpdate()} />
									</span>
								)}
							</Box>
							<Title>
								{data.goal!.completedAt ? (
									<Strikethrough>{localize(data.goal!.title).text}</Strikethrough>
								) : (
									localize(data.goal!.title).text
								)}
								{data.goal!.viewerCanViewComments && (
									<Box ml={2} display="inline-block" verticalAlign="middle">
										<Stat
											icon={<ConversationIcon />}
											count={data.goal!.comments.length}
											onClick={() => {
												comments.current?.scrollIntoView();
											}}
										/>
									</Box>
								)}
							</Title>
						</Flex>
						<Tags mb={3}>
							{data.goal?.feasibility && <FeasibilityTag feasibility={data.goal.feasibility} />}
							{data.goal?.dueDate && (
								<Tags.Item
									label={t('goals.show.dueDate.label')}
									value={moment(data.goal?.dueDate).format('D MMM YYYY')}
								/>
							)}
							{data.goal?.competency && (
								<Tags.Item
									label={t('goals.show.competency.label')}
									value={localize(data.goal?.competency.competency.title).text}
								/>
							)}
						</Tags>
						<Box mb={3}>
							<QuillText quill={data.goal!.description} />
						</Box>
						<Author
							description={t('goals.show.author.createdAt')}
							user={data.goal!.createdBy as User}
							createdAt={data.goal!.createdAt}
						/>
					</Box>
				) : (
					<Box mt={4}>
						<Title.Skeleton mb={3} />
						<Paragraph.Skeleton mb={3} />
						<Author.Skeleton />
					</Box>
				)}
			</Container>
			<SubgoalsContainer>
				{data ? (
					<SubgoalsContext
						key={data.goal.id}
						goal={data.goal as GoalEntity}
						editable={data.goal.viewerCanUpdate}
					/>
				) : (
					<Subgoals.Skeleton />
				)}
			</SubgoalsContainer>

			{data?.goal.viewerCanViewComments !== false && (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				<Container {...({ ref: comments } as any)} py={5}>
					<Heading mb={4}>{t('goals.show.comments.heading')}</Heading>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					{data ? <Comments target={data.goal} /> : <Comments.Skeleton />}
				</Container>
			)}

			{hasAudit && (
				<Container py={5}>
					<Heading mb={4}>{t('goals.show.audit.heading')}</Heading>
					<GoalAuditTrail goalID={props.goalID} />
				</Container>
			)}
		</>
	);
}
